import { DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import mapboxgl from "mapbox-gl";

import { DRAWING_CONSTANTS } from "fond/constants";
import { getVertexCoordinates } from "fond/draw/modes/base/helper";
import { EditingLayer } from "fond/types";

import { getFeatureId, NodeTopologyConfig, pullInFeatures, refreshSnappedStatus, splitPaths, validLine as validSpan } from "../topologyHelper";

import { aerialConfig } from "./helper";

/**
 * Makes the aerial post-drag handler for topology repair.
 *
 * Handles:
 * 1. Pole dragged (simple_select): merge duplicate poles, remove degenerate spans,
 *    split spans at pole location, refresh snapped status.
 * 2. Span vertex dragged (direct_select): find the pole at the new vertex location,
 *    refresh its snapped status, check span validity.
 *
 * Designed to be used as an EditingLayer.onAfterDrag callback.
 * Called via `.call(this, draggedFeature, coordPath, connectedIds)`.
 */
export const makeOnAfterDragAerial = (config: NodeTopologyConfig): NonNullable<EditingLayer["onAfterDrag"]> =>
  function onAfterDragAerial(
    this: DrawCustomModeThis,
    draggedFeature: DrawFeature,
    coordPath: string | undefined,
    connectedIds: Set<string | number>
  ): void {
    if (draggedFeature.type === "Point") {
      handlePoleDrag.call(this, config, draggedFeature as DrawPoint, connectedIds);
    } else if (draggedFeature.type === "LineString" && coordPath !== undefined) {
      handleSpanVertexDrag.call(this, config, draggedFeature as DrawLineString, coordPath);
    }
  };

/**
 * Aerial post-drag handler bound to the planner span/pole layers.
 */
export const onAfterDragAerial = makeOnAfterDragAerial(aerialConfig);

/**
 * Post-drag topology repair for a pole that was dragged.
 * Extracted from simpleSelectAerial.onMouseUp.
 */
function handlePoleDrag(this: DrawCustomModeThis, config: NodeTopologyConfig, pole: DrawPoint, connectedIds: Set<string | number>): void {
  // Find NEW colocated poles/spans at the drop location, excluding features
  // that were already connected and moved with the pole during drag.
  const { lines: spans, points: poles } = pullInFeatures.call(this, config, pole, connectedIds, "mouseup");

  // Reconstruct the connected spans (features that moved with the pole) from connectedIds
  const connectedSpans = Array.from(connectedIds)
    .map((id) => this.getFeature(id as string))
    .filter((f): f is DrawLineString => f?.type === "LineString");

  if (poles.length > 0) {
    // Pole-on-pole: merge — delete the other pole(s) so the dragged one remains selected
    poles.forEach((otherPole) => {
      const poleId = getFeatureId(otherPole);
      if (poleId) {
        this.deleteFeature(poleId as string, { silent: true });
      }
    });

    connectedSpans.forEach((original: DrawLineString) => {
      // Remove degenerate spans (both endpoints same after merge)
      if (!validSpan(original)) {
        this.deleteFeature(original.id as string, { silent: true });
      }

      // Remove duplicate colocated spans (connected span now matches a span at drop location)
      spans.forEach((span: DrawLineString) => {
        if (booleanEqual(span, original, { precision: 6 })) {
          this.deleteFeature(span.id as string, { silent: true });
        }
      });
    });

    // Refresh snapped status — the pole may have absorbed the other pole's connections
    refreshSnappedStatus.call(this, config, pole);

    // Update snapped status based on whether the pole has connections
    const isSnapped = connectedIds.size > 0 || spans.length > 0;
    pole.setProperty(DRAWING_CONSTANTS.Unsnapped, (!isSnapped).toString());
  } else if (spans.length > 0 && config.lineLayerId) {
    // Pole-on-span: split each span at the pole location.
    // Multiple spans can exist at a T-intersection; we must attempt to split all of them.
    const spanConfig = { ...config, lineLayerId: config.lineLayerId };
    const newPaths = spans.flatMap((span) => splitPaths.call(this, spanConfig, span, pole));

    // Remove connected spans that are now duplicates of the new split paths
    connectedSpans.forEach((original: DrawLineString) => {
      newPaths.forEach((span: DrawLineString) => {
        if (booleanEqual(original, span, { precision: 6 })) {
          this.deleteFeature(original.id as string, { silent: true });
        }
      });
    });

    // Refresh snapped status — pole now has spans connected after the split
    refreshSnappedStatus.call(this, config, pole);
  }
}

/**
 * Post-drag topology repair for a span vertex that was dragged.
 * The colocated pole (if any) was already moved by the base topological editing.
 * Here we refresh the pole's snapped status and check span validity.
 */
function handleSpanVertexDrag(this: DrawCustomModeThis, config: NodeTopologyConfig, span: DrawLineString, coordPath: string): void {
  const vertexCoords = getVertexCoordinates(span, coordPath);
  if (!vertexCoords) return;

  // Check if the span is now degenerate (all vertices at the same location)
  if (!validSpan(span)) {
    this.deleteFeature(span.id as string, { silent: true });
    return;
  }

  // Find poles at the new vertex location and refresh their snapped status
  const { x, y } = this._ctx.map.project(vertexCoords as mapboxgl.LngLatLike);
  const queryBbox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
    [x - 15, y - 15],
    [x + 15, y + 15],
  ];
  const nearbyFeatures = this.featuresAt(null, queryBbox, "click");

  nearbyFeatures.forEach((f) => {
    const featureId = f.properties?.id;
    if (!featureId) return;
    const drawFeature = this.getFeature(featureId);
    if (drawFeature?.type === "Point" && drawFeature.properties?.layerId === config.pointLayerId) {
      refreshSnappedStatus.call(this, config, drawFeature as DrawPoint);
    }
  });
}
